import { PropsWithChildren } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Controller,
  Control,
  FieldValues,
  FieldPath,
  FieldErrors,
  UseControllerProps,
} from 'react-hook-form';
import FormField from '@ingka/form-field';
import InputField, { InputFieldProps } from '@ingka/input-field';

import { getter } from '@/lib/utils/object';

import styles from './index.module.css';
import { extractErrorMessage } from '../error-message';

export type ControlledInputProps<T extends FieldValues> = {
  className?: string;
  // Form
  name: FieldPath<T>;
  control: Control<T, any>;
  errors: FieldErrors<T>;
  rules?: UseControllerProps<T>['rules'];
  forcedError?: any;
  // Ingka form field props
  type: InputFieldProps['type'];
  label: string;
  readOnly?: boolean;
  shouldValidate?: boolean;
  successMessage?: string;
  onChange?: () => void;
  ssrIcon?: InputFieldProps['ssrIcon'];
};

export function ControlledInput<T extends FieldValues>(
  props: PropsWithChildren<ControlledInputProps<T>>
) {
  const { t } = useTranslation();
  const {
    name,
    control,
    errors,
    rules,
    shouldValidate = true,
    label,
    readOnly,
    successMessage,
    type,
    onChange,
    forcedError,
    ssrIcon,
  } = props;
  // Error handling
  const error = forcedError || getter(errors, name);
  const errorMsgArgs = error && extractErrorMessage(error);
  const errorMsg = error && t(...errorMsgArgs);

  return (
    <div className={props.className}>
      <Controller
        name={name}
        control={control}
        rules={rules}
        render={({ field }) => (
          <FormField
            fieldHelper={{
              msg: shouldValidate ? successMessage : '',
              type: 'error',
            }}
            validation={{
              msg: errorMsg,
              type: 'error',
            }}
            valid={!error}
            shouldValidate={!!shouldValidate && error}
          >
            <InputField
              {...field}
              onChange={(e) => {
                field.onChange(e);
                onChange?.();
              }}
              type={type}
              className={styles.input}
              iconPosition="leading"
              id={name}
              label={label}
              readOnly={readOnly}
              value={field.value || ''}
              ssrIcon={ssrIcon}
              data-testid={name}
            />
          </FormField>
        )}
      />
    </div>
  );
}

export default ControlledInput;
